import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Clock, Zap, CalendarClock } from "lucide-react";
import api from "@/lib/api";
import { SESSIONS, isOpen, minutesToChange, forexWeekendClosed, fmtDuration } from "@/lib/sessions";

function useNow(ms = 15000) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const id = setInterval(() => setNow(new Date()), ms); return () => clearInterval(id); }, [ms]);
  return now;
}

export function useSessionStatuses() {
  const now = useNow(30000);
  return {
    now,
    rows: SESSIONS.map((s) => {
      const open = isOpen(s, now);
      const mins = minutesToChange(s, now);
      return { id: s.id, open, mins, status: open ? "open" : mins != null && mins <= 120 ? "next" : "closed" };
    }),
  };
}

export function usePublicNextHighImpact() {
  const [ev, setEv] = useState(null);
  useEffect(() => { api.get("/calendar/next-high").then((r) => setEv(r.data?.event || null)).catch(() => {}); }, []);
  return ev;
}

function useNextHighImpact(enabled) {
  const [events, setEvents] = useState([]);
  useEffect(() => {
    if (!enabled) return;
    api.get("/calendar/events", { params: { impact: "high" } }).then((r) => setEvents(r.data.events || [])).catch(() => {});
  }, [enabled]);
  return events;
}

function SessionRow({ s, now, t }) {
  const open = isOpen(s, now);
  const mins = minutesToChange(s, now);
  return (
    <div data-testid={`sessions-clock-${s.id}`} className={`rounded-xl border px-3 py-2.5 ${open ? "border-emerald-200 bg-emerald-50/60" : "border-slate-200 bg-white"}`}>
      <div className="flex items-center gap-2">
        <span className={`h-2 w-2 rounded-full ${open ? "bg-emerald-500 animate-pulse" : "bg-slate-300"}`} />
        <span className="text-sm font-semibold text-slate-900">{t(`sessions.${s.id}`)}</span>
        <span className={`ml-auto text-[10px] uppercase tracking-widest font-bold ${open ? "text-emerald-700" : "text-slate-400"}`}>
          {open ? t("sessions.open") : t("sessions.closed")}
        </span>
      </div>
      <div className="mt-1 text-[11px] text-slate-500 tabular-nums">
        {open ? t("sessions.closesIn", { d: fmtDuration(mins) }) : t("sessions.opensIn", { d: fmtDuration(mins) })}
      </div>
    </div>
  );
}

export default function SessionsClock({ showNews = true }) {
  const { t, i18n } = useTranslation();
  const now = useNow();
  const events = useNextHighImpact(showNews);
  const lang = i18n.language || "en";
  const local = now.toLocaleTimeString(lang, { hour: "numeric", minute: "2-digit" });
  const et = now.toLocaleTimeString(lang, { hour: "numeric", minute: "2-digit", timeZone: "America/New_York" });
  const overlap = isOpen(SESSIONS[2], now) && isOpen(SESSIONS[3], now);
  const weekend = forexWeekendClosed(now);
  const next = useMemo(() => events.find((e) => new Date(e.datetime_utc) > now), [events, now]);

  return (
    <section data-testid="sessions-clock" className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Clock size={16} className="text-blue-800" />
        <h2 className="text-sm font-semibold text-slate-900">{t("sessions.title")}</h2>
        <span data-testid="sessions-clock-local" className="ml-auto font-mono text-sm font-bold text-slate-900 tabular-nums">{local}</span>
        <span data-testid="sessions-clock-et" className="text-[11px] text-slate-400 tabular-nums">{et} ET</span>
      </div>
      {weekend ? (
        <div data-testid="sessions-clock-weekend" className="rounded-xl bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-600">{t("sessions.weekend")}</div>
      ) : overlap && (
        <div data-testid="sessions-clock-overlap" className="rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-xs font-semibold text-amber-800 inline-flex items-center gap-1.5 w-full">
          <Zap size={13} /> {t("sessions.overlap")}
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        {SESSIONS.map((s) => <SessionRow key={s.id} s={s} now={now} t={t} />)}
      </div>
      {showNews && next && (
        <div data-testid="sessions-clock-next-news" className="flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50/60 px-3 py-2 text-xs">
          <CalendarClock size={13} className="text-rose-600 shrink-0" />
          <span className="font-semibold text-rose-700 uppercase tracking-widest text-[10px]">{t("sessions.nextNews")}</span>
          <span className="truncate text-slate-800">{next.flag} {next.event}</span>
          <span className="ml-auto font-mono font-semibold text-slate-900 tabular-nums shrink-0">
            {t("sessions.inTime", { d: fmtDuration(Math.max(1, Math.round((new Date(next.datetime_utc) - now) / 60000))) })}
          </span>
        </div>
      )}
    </section>
  );
}
